/** A list sort the backend understands; `get` repeats it locally for a backend that still returns a bare array. */
export type SortOption<T> = {
	label: string
	sort: string
	order: "asc" | "desc"
	get: (row: T) => string | number | null | undefined
}

export const DEFAULT_SORT = "default"

export const sortId = (option: Pick<SortOption<unknown>, "sort" | "order">) => `${option.sort}:${option.order}`

export const findSort = <T,>(options: SortOption<T>[], id?: string) =>
	id ? options.find(option => sortId(option) === id) : undefined

/** Select options with "Default order" first; its value is DEFAULT_SORT. */
export const sortSelectValues = <T,>(options: SortOption<T>[]) => [
	{ label: "Default order", value: DEFAULT_SORT },
	...options.map(option => ({ label: option.label, value: sortId(option) }))
]

/** `sort` / `order` request fields; empty for the default order, which the backend wants omitted. */
export const sortQuery = <T,>(option?: SortOption<T>) =>
	option ? { sort: option.sort, order: option.order } : {}

const isEmpty = (value: unknown) => value === null || value === undefined || value === ""

/** Same rules as the backend: empty values last, text case-insensitive with numeric parts, stable on ties. */
export const sortRows = <T,>(rows: T[], option?: SortOption<T>) => {
	if (!option) return rows
	const dir = option.order === "asc" ? 1 : -1
	return rows.map((row, index) => ({ row, index, value: option.get(row) }))
		.sort((a, b) => {
			const aEmpty = isEmpty(a.value)
			const bEmpty = isEmpty(b.value)
			if (aEmpty || bEmpty) return aEmpty === bEmpty ? a.index - b.index : aEmpty ? 1 : -1
			const diff = typeof a.value === "number" && typeof b.value === "number"
				? a.value - b.value
				: String(a.value).localeCompare(String(b.value), undefined, { sensitivity: "base", numeric: true })
			return diff ? diff * dir : a.index - b.index
		})
		.map(item => item.row)
}

/** Case-insensitive substring match over the given fields, like the backend `search`. */
export const textMatches = (query: string, values: unknown[]) => {
	const needle = query.trim().toLowerCase()
	return !needle || values.some(value => !isEmpty(value) && String(value).toLowerCase().includes(needle))
}
